import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormLabel, type FormTypography } from './FormLabel';

interface FormFieldProps {
  id: string;
  label?: string;
  tooltip?: ReactNode;
  required?: boolean;
  children: ReactNode;
  feedback?: ReactNode;
  className?: string;
  /** Composite widgets label themselves with aria-labelledby instead. */
  labelFor?: string | false;
  typography?: FormTypography;
}

/**
 * Structural shell for one label, one control, and its feedback.
 *
 * Keeping these pieces under a single flex item prevents a parent form's gap
 * from leaking between a label, control, and error. Parent layouts now own only
 * field-to-field spacing; this component owns the consistent 8px internal gap.
 */
export const FormField = ({
  id,
  label,
  tooltip,
  required,
  children,
  feedback,
  className,
  labelFor,
  typography,
}: FormFieldProps) => (
  <div
    className={classNames('flex w-full min-w-0 flex-col gap-2 text-left', className)}
    data-form-field={id}
  >
    <FormLabel
      id={`${id}-label`}
      htmlFor={labelFor === false ? undefined : (labelFor ?? id)}
      label={label}
      tooltip={tooltip}
      required={required}
      typography={typography}
    />
    {children}
    {feedback}
  </div>
);
